// Copyright (c) Microsoft. All rights reserved.

import {
    Button,
    Label,
    makeStyles,
    Persona,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    SelectTabEventHandler,
    shorthands,
    Tab,
    TabList,
    tokens,
    Tooltip,
    Text
} from '@fluentui/react-components';
import { Edit24Filled, EditRegular, Person16Regular } from '@fluentui/react-icons';
import React, { useState } from 'react';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { FeatureKeys } from '../../redux/features/app/AppState';
import { Alerts } from '../shared/Alerts';
import { ChatRoom } from './ChatRoom';
import { ParticipantsList } from './controls/ParticipantsList';
import { EditChatName } from './shared/EditChatName';
import { DocumentsTab } from './tabs/DocumentsTab';
import { PersonaTab } from './tabs/PersonaTab';
import { PlansTab } from './tabs/PlansTab';
import { PromptsTab } from './tabs/PromptsTab';
import { useDispatch } from 'react-redux';
import { setSelectedChatWindowTab } from '../../redux/features/app/appSlice';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        backgroundColor: tokens.colorNeutralBackground3,
        boxShadow: 'rgb(0 0 0 / 25%) 0 0.2rem 0.4rem -0.075rem',
    },
    header: {
        ...shorthands.borderBottom('1px', 'solid', 'rgb(0 0 0 / 10%)'),
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
        backgroundColor: tokens.colorNeutralBackground4,
        display: 'flex',
        flexDirection: 'row',
        boxSizing: 'border-box',
        width: '100%',
        justifyContent: 'space-between',
    },
    title: {
        ...shorthands.gap(tokens.spacingHorizontalM),
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'row',
    },
    controls: {
        display: 'flex',
        alignItems: 'center',
    },
    popoverHeader: {
        ...shorthands.margin('0'),
        paddingBottom: tokens.spacingVerticalXXS,
        fontStyle: 'normal',
        fontWeight: '600',
    },
    popover: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        ...shorthands.padding(tokens.spacingVerticalXXL),
        ...shorthands.gap(tokens.spacingVerticalMNudge),
        width: '398px',
    },
    input: {
        width: '100%',
    },
    buttons: {
        display: 'flex',
        alignSelf: 'end',
        ...shorthands.gap(tokens.spacingVerticalS),
    },
    alerts: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.margin(0, '72px'),
    },
    chatTypeIndicator: {
        fontStyle: 'normal',
        fontWeight: '600',
        fontSize: tokens.fontSizeBase400,
        display: 'flex',
        alignItems: 'center',
        paddingRight: tokens.spacingHorizontalM,

    }
});

export const ChatWindow: React.FC = () => {
    const classes = useClasses();
    const { features, selectedChatWindowTab } = useAppSelector((state: RootState) => state.app);
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const chatName = conversations[selectedId].title;
    const dispatch = useDispatch();

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const onTabSelect: SelectTabEventHandler = (_event, data) => {
        dispatch(setSelectedChatWindowTab(data.value));
    };

    function isDefaultLitiumChat() {
        return conversations[selectedId].personaId ? conversations[selectedId].personaId === "00000000-0000-0000-0000-000000000000" : true;
    }

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <div className={classes.title}>
                    {!features[FeatureKeys.SimplifiedExperience].enabled && (
                        <>
                            <Persona
                                key={'Semantic Kernel Bot'}
                                size="medium"
                                avatar={{image: {src: conversations[selectedId].botProfilePicture}}}
                                presence={{status: 'available'}}
                            />
                            <Label size="large" weight="semibold">
                                {chatName}
                            </Label>
                            <Popover open={isEditing}>
                                <PopoverTrigger disableButtonEnhancement>
                                    <Tooltip content={'Edit conversation name'} relationship="label">
                                        <Button
                                            data-testid="editChatTitleButton"
                                            icon={isEditing ? <Edit24Filled/> : <EditRegular/>}
                                            appearance="transparent"
                                            onClick={() => {
                                                setIsEditing(true);
                                            }}
                                            disabled={!chatName}
                                            aria-label="Edit conversation name"
                                        />
                                    </Tooltip>
                                </PopoverTrigger>
                                <PopoverSurface className={classes.popover}>
                                    <h3 className={classes.popoverHeader}>Bot name</h3>
                                    <EditChatName
                                        name={chatName}
                                        chatId={selectedId}
                                        exitEdits={() => {
                                            setIsEditing(false);
                                        }}
                                        textButtons
                                    />
                                </PopoverSurface>
                            </Popover>
                        </>
                    )}
                    <TabList selectedValue={selectedChatWindowTab} onTabSelect={onTabSelect}>
                        <Tab data-testid="chatTab" id="chat" value="chat" aria-label="Chat Tab" title="Chat Tab">
                            Chat
                        </Tab>
                        <Tab
                            data-testid="documentsTab"
                            id="documents"
                            value="documents"
                            aria-label="Documents Tab"
                            title="Documents Tab"
                        >
                            Documents
                        </Tab>
                        <Tab
                            data-testid="promptsTab"
                            id="prompts"
                            value="prompts"
                            aria-label="Prompts Tab"
                            title="Prompts Tab"
                        >
                            Prompts
                        </Tab>
                        {features[FeatureKeys.PluginsPlannersAndPersonas].enabled && (
                            <>
                                <Tab
                                    data-testid="personaTab"
                                    id="persona"
                                    value="persona"
                                    icon={<Person16Regular/>}
                                    aria-label="Persona Tab"
                                    title="Persona Tab">
                                    Persona
                                </Tab>
                            </>
                        )}
                    </TabList>
                </div>
                <div className={classes.title}>
                    <div className={classes.controls}>
                        { conversations[selectedId].users.length > 1 && (
                            <ParticipantsList participants={conversations[selectedId].users}/>
                        )}
                    </div>
                    <Text className={classes.chatTypeIndicator}>{isDefaultLitiumChat() ? "Litium Persona" : "Chat Persona: " + conversations[selectedId].personaName}</Text>
                </div>
            </div>
            {selectedChatWindowTab === 'chat' && <ChatRoom/>}
            {selectedChatWindowTab === 'documents' && <DocumentsTab/>}
            {selectedChatWindowTab === 'prompts' && <PromptsTab/>}
            {selectedChatWindowTab === 'plans' && (
                <PlansTab
                    setChatTab={() => {
                        dispatch(setSelectedChatWindowTab('chat'));
                    }}
                />
            )}
            {selectedChatWindowTab === 'persona' && <PersonaTab/>}
            {selectedChatWindowTab !== 'chat' && (
                <div className={classes.alerts}>
                    <Alerts />
                </div>
            )}
        </div>
    );
};
